import type { AiDecision, AiRun } from '../../../api/DigiobcanApi';
import { adminGetAiDecisions } from '../../../features/queries/adminGetAiDecisions';
import { adminGetAiDecisionsStats } from '../../../features/queries/adminGetAiDecisionsStats';
import { adminGetAiRuns } from '../../../features/queries/adminGetAiRuns';
import {
  AI_DECISION_TYPE_LABEL,
  AI_RUN_STATUS,
  DRY_RUN_LABEL,
  type AiDecisionType,
  aiDecisionResult,
  aiDecisionScore,
  humanAgreement,
  latency,
  percent,
} from '../../../lib/aiDecisions';
import { entityId, shortId } from '../../../lib/ids';
import { useInfiniteQ, useLoadPagesThrough, useQ } from '../../../lib/query';
import { useLayout } from '../../../providers/LayoutProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import { __, toastError } from '../../../utils';
import AiDecisionDialog from './AiDecisionDialog';
import AiDecisionsStats from './AiDecisionsStats';
import {
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  FilterPanel,
  Pagination,
  Stack,
  StatusChip,
  Typography,
  optionLabel,
  selectedValues,
  useUrlFilters,
  type Column,
  type FilterField,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from 'date-fns';
import React from 'react';
import { useSearchParams } from 'react-router';

const LIMIT = 25;

/**
 * Kým niečo čaká alebo beží, pýta sa častejšie - skúšobný podnet trvá
 * desiatky sekúnd a úradník čaká práve na to. Naprázdno stačí zachytiť
 * ostrý podnet, ktorý medzitým prišiel.
 */
const RUNS_POLL_BUSY_MS = 5_000;
const RUNS_POLL_IDLE_MS = 30_000;

/**
 * Riadok tabuľky: rozhodnutie, alebo beh, ktorý ho ešte nemá. Beh sa do
 * tvaru rozhodnutia prekladá (`runRow`), aby stĺpce ostali jedny - chýbajúce
 * istota, zhoda a latencia sa vykreslia prázdne samy.
 */
type Row = AiDecision & { run?: AiRun };

const runRow = (run: AiRun): Row => ({
  decisionId: run.runId,
  type: run.type,
  reportId: run.reportId,
  reportTitle: run.reportTitle,
  insertedAt: run.startedAt ?? run.queuedAt,
  model: run.model,
  promptVersion: run.promptVersion,
  dryRun: run.dryRun,
  run,
});

/** `2026-08-24` → hranice dňa, lebo API berie `date-time`. */
const dayStart = (value?: string) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined;
const dayEnd = (value?: string) =>
  value ? new Date(`${value}T23:59:59.999Z`).toISOString() : undefined;

const ONLY_ERRORS_LABEL = __('Len chyby');

/**
 * Skúšobné behy ako prepínač, nie výber „Beh" s dvoma hodnotami. Výber
 * dával aj „len ostré", ale to sa nikto nepýtal - skúšobné sú výnimka, ktorú
 * úradník hľadá po spustení z administrácie podnetov, a v bežnom zozname
 * ich odlíši štítok pri type. Vypnutý prepínač preto neznamená „len ostré",
 * ale všetko; API dostane `dryRun` len so zapnutým.
 */
const ONLY_DRY_RUN_LABEL = __('Len skúšobné');

/**
 * Prehľad rozhodnutí AI: každý beh kategorizácie, moderácie a hľadania
 * duplicít nad podnetmi, od najnovšieho, s pásom čísel nad tabuľkou.
 *
 * Vzor je správa podnetov ([[zoznamy]]): lišta filtrov v hlavičke, na
 * desktope trvalý panel v `aside`, na mobile sheet, stránkovanie v pätičke.
 * Hľadanie tu nie je - API `q` nepozná a hľadanie v jednej načítanej strane
 * by klamalo.
 *
 * Riadok neotvára stránku, ale dialóg (`onRowClick`, nie `rowHref`): všetko,
 * čo o behu vieme, prišlo už v zozname.
 *
 * Navrchu prvej strany stoja behy, ktoré ešte nemajú rozhodnutie - čakajú
 * v rade reports-analyzer alebo bežia (`GET /admin/ai-runs`). Tie dialóg
 * nemajú čím naplniť, takže vedú `rowHref` na detail podnetu. Keď beh
 * zmizne, jeho rozhodnutie práve pribudlo - zoznam aj súhrn sa obnovia.
 *
 * Podnet je filter **mimo schémy** (`?podnet=`): nastavuje sa z dialógu
 * („Všetky behy podnetu") a v paneli nemá čo robiť - kto ho chce zrušiť,
 * zruší čip.
 */
const AiDecisionsAdminPage: React.FC = () => {
  const { isMd } = useResponsive();
  const { setFooter, setAside, setPageFilters } = useLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [opened, setOpened] = React.useState<AiDecision | null>(null);

  // Ponuka modelov a promptov pochádza zo súhrnu **bez filtrov** - inak by po
  // výbere jedného modelu ostatné z ponuky zmizli a nedalo by sa pridať druhý.
  const { data: allStats } = useQ(adminGetAiDecisionsStats, {});
  const knownModels = React.useMemo(
    () => [...new Set((allStats?.groups ?? []).map((g) => g.model))].sort(),
    [allStats],
  );
  const knownPrompts = React.useMemo(
    () =>
      [...new Set((allStats?.groups ?? []).map((g) => g.promptVersion))].sort(),
    [allStats],
  );

  const filterFields: FilterField[] = React.useMemo(
    () => [
      {
        key: 'type',
        label: __('Typ'),
        type: 'select',
        options: (Object.keys(AI_DECISION_TYPE_LABEL) as AiDecisionType[]).map(
          (value) => ({ value, label: AI_DECISION_TYPE_LABEL[value] }),
        ),
        quick: true,
      },
      {
        key: 'model',
        label: __('Model'),
        type: 'select',
        options: knownModels,
        quick: true,
      },
      {
        key: 'promptVersion',
        label: __('Verzia promptu'),
        type: 'select',
        options: knownPrompts,
      },
      {
        key: 'onlyDryRun',
        label: ONLY_DRY_RUN_LABEL,
        type: 'toggle',
        quick: true,
      },
      {
        key: 'onlyErrors',
        label: ONLY_ERRORS_LABEL,
        type: 'toggle',
        quick: true,
      },
      {
        key: 'dateFrom',
        label: __('Od'),
        type: 'date',
        group: __('Dátum behu'),
      },
      {
        key: 'dateTo',
        label: __('Do'),
        type: 'date',
        group: __('Dátum behu'),
      },
    ],
    [knownModels, knownPrompts],
  );

  const {
    filters,
    setFilters,
    page: pageIndex,
    setPage: setPageIndex,
    activeCount,
    clearAll: clearFilters,
    patch,
  } = useUrlFilters({ searchParams, setSearchParams, fields: filterFields });

  const reportFilter = searchParams.get('podnet') ?? undefined;
  const setReportFilter = React.useCallback(
    (reportId: string | undefined) =>
      patch({ podnet: reportId ? shortId(reportId) : undefined }),
    [patch],
  );

  const clearAll = React.useCallback(() => {
    clearFilters();
    patch({ podnet: undefined });
  }, [clearFilters, patch]);

  /** Preklad stavu obrazovky na parametre API - jeden pre zoznam aj súhrn. */
  const query = React.useMemo(
    () => ({
      type: selectedValues(filters, 'type') as AiDecisionType[],
      model: selectedValues(filters, 'model'),
      promptVersion: selectedValues(filters, 'promptVersion'),
      onlyErrors: filters.onlyErrors ? true : undefined,
      dryRun: filters.onlyDryRun ? true : undefined,
      reportId: reportFilter ? entityId(reportFilter, 'report') : undefined,
      dateFrom: dayStart((filters.dateFrom as string) || undefined),
      dateTo: dayEnd((filters.dateTo as string) || undefined),
    }),
    [filters, reportFilter],
  );

  // Chyba ani dátum v minulosti na nedobehnutý beh sedieť nemôžu.
  const showRuns =
    !query.onlyErrors && !(query.dateTo && new Date(query.dateTo) < new Date());
  const { data: runsData } = useQ(
    adminGetAiRuns,
    {
      type: query.type,
      model: query.model,
      promptVersion: query.promptVersion,
      dryRun: query.dryRun,
      reportId: query.reportId,
    },
    {
      enabled: showRuns,
      refetchInterval: (q) =>
        q.state.data?.runs.length ? RUNS_POLL_BUSY_MS : RUNS_POLL_IDLE_MS,
    },
  );
  const runs = React.useMemo(
    () => (showRuns ? (runsData?.runs ?? []) : []),
    [showRuns, runsData],
  );

  // Beh, ktorý z radu zmizol, dobehol - jeho rozhodnutie je už v tabuľke.
  const queryClient = useQueryClient();
  const previousRunIds = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const current = new Set(runs.map((run) => run.runId));
    const finished = [...previousRunIds.current].some(
      (runId) => !current.has(runId),
    );
    previousRunIds.current = current;

    if (finished) {
      queryClient.invalidateQueries({
        queryKey: [adminGetAiDecisions.toString()],
      });
      queryClient.invalidateQueries({
        queryKey: [adminGetAiDecisionsStats.toString()],
      });
    }
  }, [runs, queryClient]);

  const { data: stats, isLoading: statsLoading } = useQ(
    adminGetAiDecisionsStats,
    query,
  );

  const {
    data = { pages: [] },
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQ(
    adminGetAiDecisions,
    { limit: LIMIT, ...query },
    {
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        lastPage.hasNextOffset ? lastPage.offset + LIMIT : undefined,
    },
  );

  // F5 na `?page=2`: nekonečný dotaz začína od prvej strany, takže sa musí
  // dočítať až po tú z adresy (`useLoadPagesThrough`).
  useLoadPagesThrough({
    pageIndex,
    loadedPages: data.pages.length,
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || isFetchingNextPage,
    fetchNextPage,
    setPageIndex,
  });

  const rows: Row[] = React.useMemo(() => {
    const decisions = data.pages[pageIndex]?.decisions ?? [];
    return pageIndex === 0 ? [...runs.map(runRow), ...decisions] : decisions;
  }, [data, pageIndex, runs]);

  React.useEffect(() => {
    if (isError) {
      toastError(__('Rozhodnutia AI sa nepodarilo načítať.'));
    }
  }, [isError]);

  const columns: Column<Row>[] = React.useMemo(
    () => [
      {
        key: 'insertedAt',
        label: __('Kedy'),
        width: '140px',
        minWidth: '140px',
        render: (d) => formatDate(new Date(d.insertedAt), 'dd.MM.yyyy HH:mm'),
      },
      {
        key: 'type',
        label: __('Typ'),
        width: '120px',
        minWidth: '120px',
        // Skúšobný beh má štítok pod typom, nie vlastný stĺpec: je ich málo
        // a stĺpec by bol takmer celý prázdny.
        render: (d) => (
          <Stack gap={0.5} alignItems='flex-start'>
            <StatusChip tone={d.type === 'explicit' ? 'info' : 'neutral'}>
              {AI_DECISION_TYPE_LABEL[d.type]}
            </StatusChip>
            {d.dryRun ? (
              <StatusChip tone='warning'>{DRY_RUN_LABEL}</StatusChip>
            ) : null}
          </Stack>
        ),
      },
      {
        key: 'reportTitle',
        label: __('Podnet'),
        width: '2fr',
        minWidth: '200px',
        strong: true,
        render: (d) => (
          <Stack sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {d.reportTitle}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                fontFamily: 'monospace',
              }}
            >
              {shortId(d.reportId)}
            </Typography>
          </Stack>
        ),
      },
      {
        key: 'result',
        label: __('Výsledok'),
        width: '1.4fr',
        minWidth: '160px',
        render: (d) =>
          d.run ? (
            <StatusChip tone={AI_RUN_STATUS[d.run.status].tone}>
              {AI_RUN_STATUS[d.run.status].label}
            </StatusChip>
          ) : d.error ? (
            <StatusChip tone='error'>{__('Zlyhalo')}</StatusChip>
          ) : (
            <Typography
              sx={{
                fontSize: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {aiDecisionResult(d)}
            </Typography>
          ),
      },
      {
        key: 'score',
        label: __('Istota'),
        width: '80px',
        minWidth: '80px',
        numeric: true,
        render: (d) => percent(aiDecisionScore(d)),
      },
      {
        key: 'humanAgrees',
        label: __('Úradník'),
        width: '110px',
        minWidth: '110px',
        render: (d) => {
          const agreement = humanAgreement(d);
          return agreement ? (
            <StatusChip tone={agreement.tone}>{agreement.label}</StatusChip>
          ) : null;
        },
      },
      {
        key: 'model',
        label: __('Model'),
        width: '1.2fr',
        minWidth: '160px',
        mono: true,
        render: (d) => (
          <Typography
            title={`${d.model} · ${d.promptVersion}`}
            sx={{
              fontSize: 12,
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {d.model}
          </Typography>
        ),
      },
      {
        key: 'latencyMs',
        label: __('Latencia'),
        width: '90px',
        minWidth: '90px',
        numeric: true,
        render: (d) => latency(d.latencyMs),
      },
    ],
    [],
  );

  const activeChips = React.useMemo(() => {
    const chips: {
      key: string;
      label: string;
      value: string;
      remove: () => void;
    }[] = [];

    filterFields
      .filter((field) => field.type === 'select')
      .forEach((field) => {
        selectedValues(filters, field.key).forEach((value) => {
          chips.push({
            key: `${field.key}-${value}`,
            label: field.label,
            value: optionLabel(field, value),
            remove: () =>
              setFilters({
                ...filters,
                [field.key]: selectedValues(filters, field.key).filter(
                  (item) => item !== value,
                ),
              }),
          });
        });
      });

    if (reportFilter) {
      chips.push({
        key: 'podnet',
        label: __('Podnet'),
        value: reportFilter,
        remove: () => setReportFilter(undefined),
      });
    }

    if (filters.dateFrom || filters.dateTo) {
      chips.push({
        key: 'date',
        label: __('Dátum behu'),
        value: [filters.dateFrom, filters.dateTo]
          .filter(Boolean)
          .map((value) => formatDate(new Date(value as string), 'dd.MM.yyyy'))
          .join(' - '),
        remove: () => setFilters({ ...filters, dateFrom: '', dateTo: '' }),
      });
    }

    return chips;
  }, [filterFields, filters, setFilters, reportFilter, setReportFilter]);

  const hasNext =
    (pageIndex < data.pages.length - 1 || !!hasNextPage) && !isFetchingNextPage;

  React.useEffect(() => {
    setFooter(
      <Pagination
        page={pageIndex}
        hasNext={hasNext}
        onPageChange={(next) => {
          if (next >= data.pages.length && hasNextPage) {
            fetchNextPage();
          }
          setPageIndex(next);
        }}
      />,
    );

    return () => setFooter(null);
  }, [
    setFooter,
    setPageIndex,
    pageIndex,
    hasNext,
    hasNextPage,
    fetchNextPage,
    data.pages.length,
  ]);

  // Na desktope je filter trvalý stĺpec v shelle, na mobile sheet (viď správa
  // podnetov). Rýchle polia sú v lište, panel ich preto nekreslí druhýkrát.
  React.useEffect(() => {
    if (!isMd) {
      setAside(null);
      return;
    }

    setAside(
      <FilterPanel
        variant='inline'
        fields={filterFields}
        hideQuickFields
        value={filters}
        onChange={setFilters}
        title={__('Filtrovanie')}
        defaultGroupLabel={__('Filtre')}
      />,
    );

    return () => setAside(null);
  }, [isMd, setAside, filterFields, filters, setFilters]);

  React.useEffect(() => {
    setPageFilters(
      <FilterBar
        variant='header'
        fields={filterFields}
        value={filters}
        onChange={setFilters}
        onOpenPanel={isMd ? undefined : () => setPanelOpen((open) => !open)}
        activeCount={activeCount + (reportFilter ? 1 : 0)}
        panelLabel={__('Všetky filtre')}
      >
        {activeChips.map((chip) => (
          <FilterChip
            key={chip.key}
            label={chip.label}
            value={chip.value}
            onRemove={chip.remove}
          />
        ))}
      </FilterBar>,
    );

    return () => setPageFilters(null);
  }, [
    setPageFilters,
    filterFields,
    filters,
    setFilters,
    isMd,
    activeCount,
    reportFilter,
    activeChips,
  ]);

  const table = React.useMemo(
    () => (
      <DataTable
        variant={isMd ? 'table' : 'cards'}
        columns={columns}
        rows={rows}
        rowKey='decisionId'
        onRowClick={(d: Row) => setOpened(d)}
        rowHref={(d: Row) =>
          d.run ? `/administracia/podnety/${shortId(d.reportId)}` : undefined
        }
        // Zlyhaný beh si pýta pozornosť - podnet dostal len fallback.
        rowTone={(d: Row) => (d.error ? 'error' : undefined)}
        loading={isLoading || isFetchingNextPage}
        empty={
          <EmptyState
            compact
            icon='list'
            title={__('Žiadne rozhodnutia AI')}
            description={
              activeChips.length
                ? __(
                    'Vybraným filtrom nezodpovedá žiadny beh. Skúšobné behy pribúdajú postupne, jeden podnet trvá desiatky sekúnd.',
                  )
                : __('Behy pribudnú, keď AI spracuje nový podnet.')
            }
          />
        }
      />
    ),
    [isMd, columns, rows, isLoading, isFetchingNextPage, activeChips.length],
  );

  return (
    <>
      <Stack gap={1.5} sx={{ padding: 1.5 }}>
        <AiDecisionsStats groups={stats?.groups ?? []} loading={statsLoading} />
        {table}
      </Stack>

      {panelOpen && !isMd ? (
        <FilterPanel
          variant='sheet'
          fields={filterFields}
          hideQuickFields
          value={filters}
          onChange={setFilters}
          onClose={() => setPanelOpen(false)}
          onClear={clearAll}
          applyLabel={__('Použiť')}
          clearLabel={__('Vymazať filtre')}
          title={__('Filtrovanie')}
          closeLabel={__('Zavrieť filtre')}
          defaultGroupLabel={__('Filtre')}
        />
      ) : null}

      <AiDecisionDialog
        decision={opened}
        onClose={() => setOpened(null)}
        onFilterReport={setReportFilter}
      />
    </>
  );
};

export default AiDecisionsAdminPage;
